import { unwrap } from '@swarm/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { meKey } from '../../lib/queryClient';
import type { Me } from '../identity/api';
import notes from './release-notes.json';

export interface ReleaseNote {
  version: string;
  /** ISO date of the release. */
  date: string;
  items: { text: string; /** In-app path for a "Ansehen" link. */ to?: string }[];
}

/** Notes of every release, newest first. Maintained by the release run. */
export const releaseNotes: ReleaseNote[] = notes;

/** Most versions the "Neu in swarm" dialog lists after a long absence. */
export const MAX_UNSEEN = 5;

const triple = (version: string) => version.split('.').map(Number);

/** Orders `x.y.z` versions numerically; negative if `a` is older. */
export function compareVersions(a: string, b: string): number {
  const [x, y] = [triple(a), triple(b)];
  for (let i = 0; i < 3; i++) {
    const d = (x[i] ?? 0) - (y[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * Notes the user has not acknowledged yet: newer than `lastSeen`, not newer
 * than the running version, newest first, at most {@link MAX_UNSEEN}.
 */
export function unseenNotes(lastSeen: string, running: string): ReleaseNote[] {
  return releaseNotes
    .filter(
      (n) =>
        n.items.length > 0 &&
        compareVersions(n.version, lastSeen) > 0 &&
        compareVersions(n.version, running) <= 0,
    )
    .sort((a, b) => compareVersions(b.version, a.version))
    .slice(0, MAX_UNSEEN);
}

/** Acknowledges the notes up to `version` for this person, on every device. */
export function useMarkReleaseSeen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (version: string) =>
      unwrap(await api.POST('/me/release-seen', { body: { version } })),
    onSuccess: (me: Me) => client.setQueryData(meKey, me),
  });
}
